import type { FragmentOf, ResultOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import {
  Flex,
  FlexCenter,
  FlexColumn,
  NumberDisplay,
  TextBody,
} from "@opensea/ui-kit"
import { CollectionSweepQuoteReceiptRangeFragment } from "./CollectionSweepQuoteReceiptRange.fragment"

type Props = {
  maxAmountOfItems: number
  inTooltip?: boolean
  quote: FragmentOf<typeof CollectionSweepQuoteReceiptRangeFragment>
}

type ItemQuote = (ResultOf<
  typeof CollectionSweepQuoteReceiptRangeFragment
>["quote"] & {
  __typename: "CollectionSweepQuoteSuccess"
})["itemQuotes"][0]

export function getPriceRangeFromQuote(
  quoteKey: FragmentOf<typeof CollectionSweepQuoteReceiptRangeFragment>,
  maxAmountOfItems: number,
  getPrice: (item: ItemQuote) => number,
): { min: number; max: number; tokenSymbol: string } {
  const quote = readFragment(CollectionSweepQuoteReceiptRangeFragment, quoteKey)

  if (quote.quote.__typename === "CollectionSweepQuoteSuccess") {
    const items = quote.quote.itemQuotes

    // choose the min value, in case the quote returned prices for less than maxAmountOfItems
    const numOfItems = Math.min(maxAmountOfItems, items.length)
    const lowerRange = items.slice(0, numOfItems)
    const upperRange = items.slice(items.length - numOfItems)

    const min = lowerRange.reduce((acc, p) => acc + getPrice(p), 0)
    const max = upperRange.reduce((acc, p) => acc + getPrice(p), 0)

    const tokenSymbol = items[0].pricePerItem.token.symbol
    return { min, max, tokenSymbol }
  }

  return {
    min: 0,
    max: 0,
    tokenSymbol: quote.collection.chain.nativeCurrency.symbol,
  }
}

export const CollectionSweepQuoteReceiptRange = ({
  maxAmountOfItems,
  inTooltip,
  quote: quoteKey,
}: Props) => {
  const tokenRange = getPriceRangeFromQuote(
    quoteKey,
    maxAmountOfItems,
    item => item.pricePerItem.token.unit,
  )
  const usdRange = getPriceRangeFromQuote(
    quoteKey,
    maxAmountOfItems,
    item => item.pricePerItem.usd,
  )

  if (inTooltip) {
    return (
      <FlexCenter className="grow gap-2 first:pl-0">
        <Flex className="flex-row justify-end space-x-2">
          <TextBody>
            <NumberDisplay
              suffix={tokenRange.tokenSymbol}
              value={tokenRange.max}
            />
          </TextBody>
        </Flex>

        <TextBody>{"/"}</TextBody>

        <Flex className="flex-row justify-end space-x-2">
          <TextBody>
            <NumberDisplay display="usd" value={usdRange.max} />
          </TextBody>
        </Flex>
      </FlexCenter>
    )
  }

  return (
    <FlexColumn className="grow first:pl-0">
      <Flex className="flex-row justify-end space-x-2">
        <TextBody weight="semibold">
          <NumberDisplay
            suffix={tokenRange.tokenSymbol}
            value={tokenRange.min}
          />
        </TextBody>
      </Flex>
      <Flex className="flex-row justify-end space-x-2">
        <TextBody className="font-mono" color="text-secondary">
          <NumberDisplay display="usd" value={usdRange.min} />
        </TextBody>
      </Flex>
    </FlexColumn>
  )
}
